/**
 * StarterRegistry — the house pattern (mirrors SeedRegistry /
 * DashboardSurfaceRegistry). Module-level singleton: registrations are
 * static read-only config; request-scoped DI governs backend request
 * state and does not apply here. Flag filtering takes the resolved
 * flags as a PARAMETER (same way NewFolderDialog filters products).
 */

import type { ResolvedFeatureFlags } from '../../domains/feature-flags/models/FeatureFlags'
import type { TemplateItem } from '../../dashboard/components/TemplateCard'
import type { FunctionTag, JobTag, Starter } from './Starter'

class Registry {
  private readonly byId = new Map<string, Starter>()

  register(starter: Starter): void {
    // Last registration wins (idempotent re-registration is harmless).
    this.byId.set(starter.id, starter)
  }

  get(id: string): Starter | null {
    return this.byId.get(id) ?? null
  }

  list(): Starter[] {
    return Array.from(this.byId.values())
  }

  /** Test-only. */
  __reset(): void {
    this.byId.clear()
  }
}

export const starterRegistry = new Registry()

export function registerStarter(starter: Starter): void {
  starterRegistry.register(starter)
}

export interface StarterFilter {
  function?: FunctionTag
  job?: JobTag
  popular?: boolean
  /** When provided, starters whose `requiredFlag` is off are dropped.
   *  Pass undefined while flags are loading — flagged starters stay
   *  hidden until resolution (the dialog's behavior). */
  flags?: ResolvedFeatureFlags
}

export function listStarters(filter?: StarterFilter): Starter[] {
  return starterRegistry.list().filter((s) => {
    if (filter?.function && !s.function.includes(filter.function)) return false
    if (filter?.job && !s.job.includes(filter.job)) return false
    if (filter?.popular !== undefined && !!s.popular !== filter.popular) return false
    if (s.requiredFlag && filter && 'flags' in filter) {
      if (!filter.flags?.flags?.[s.requiredFlag]) return false
    }
    return true
  })
}

export function getStarter(id: string): Starter | null {
  return starterRegistry.get(id)
}

/**
 * Adapter for surfaces still typed on TemplateItem (DashboardPage's
 * create paths consume this shape unchanged — the migration trick).
 *  - seed artifact     → native + `sample:` seed (omitted for
 *                        `pickerOnOpen` cards unless explicitly requested)
 *  - agent artifact    → native + `agent:` seed
 *  - template artifact → native, seedless picker card. The starter
 *                        catalog intentionally does not clone server
 *                        templates; generic StartIntent still supports
 *                        `template:` seeds for non-catalog links.
 *  - legacy artifact   → legacy app clone, id = legacy template id
 * Item ids preserve the historical `template-card-<id>` test ids.
 */
interface TemplateItemAdapterOptions {
  includePickerSeed?: boolean
}

export function toTemplateItem(s: Starter): TemplateItem {
  return toTemplateItemWithOptions(s, {})
}

export function toTemplateItemForTemplateUse(s: Starter): TemplateItem {
  return toTemplateItemWithOptions(s, { includePickerSeed: true })
}

function toTemplateItemWithOptions(s: Starter, options: TemplateItemAdapterOptions): TemplateItem {
  const base = { name: s.name, thumbnailUrl: s.thumbnailUrl, translationKey: s.translationKey }
  switch (s.artifact.kind) {
    case 'seed':
      return {
        ...base,
        id: s.id,
        source: 'native',
        type: s.artifact.diagramType,
        seed: s.pickerOnOpen && !options.includePickerSeed ? undefined : `sample:${s.artifact.seedId}`,
      }
    case 'agent':
      return {
        ...base,
        id: s.id,
        source: 'native',
        type: s.artifact.diagramType,
        seed: `agent:${s.artifact.promptTemplate}`,
      }
    case 'template':
      return {
        ...base,
        // Template starters are domain-picker entries, not server-clone
        // entries. Key on the starter id like seed/pickerOnOpen paths do.
        id: s.id,
        source: 'native',
        type: s.artifact.diagramType,
      }
    case 'legacy':
      return {
        ...base,
        id: s.artifact.templateId,
        source: 'legacy',
        type: s.artifact.legacyType,
      }
  }
}
